// Sentry must be imported first
import './instrument';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import './styles/support-portal.css';

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

const renderBootstrapFallback = (message: string) => {
  rootElement.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:hsl(0 0% 100%);color:hsl(222 47% 11%);font-family:system-ui,sans-serif;">
      <div style="max-width:680px;text-align:center;line-height:1.5;">
        <h1 style="font-size:20px;margin:0 0 8px;">Preview failed to render</h1>
        <p style="margin:0 0 12px;">${message}</p>
        <p style="margin:0;opacity:.75;">If your published URL works, this is likely a temporary preview environment issue.</p>
      </div>
    </div>
  `;
};

// Global error handler - report to Sentry and keep diagnostics visible
window.addEventListener('error', (event) => {
  if (event.error) {
    Sentry.captureException(event.error);
  }
  console.error('Global error caught:', event.error ?? event.message);
});

const isChunkLoadError = (reason: unknown): boolean => {
  const msg: string = (reason as Error)?.message ?? '';
  const name: string = (reason as Error)?.name ?? '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('dynamically imported module')
  );
};

window.addEventListener('unhandledrejection', (event) => {
  // Auto-reload once when stale chunks 404 after a new deploy
  if (isChunkLoadError(event.reason)) {
    if (!sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return;
    }
  }
  Sentry.captureException(event.reason);
  console.error('Unhandled promise rejection:', event.reason);
});

const bootstrap = async () => {
  try {
    const { default: App } = await import('./App.tsx');
    // Successful load — clear reload guard so the next deploy can auto-reload again
    sessionStorage.removeItem('chunk_reload');
    createRoot(rootElement).render(<App />);
  } catch (error) {
    // If bootstrap itself fails due to a stale chunk, reload once
    if (isChunkLoadError(error) && !sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return;
    }
    Sentry.captureException(error);
    console.error('Bootstrap error:', error);
    renderBootstrapFallback('The app crashed during startup. Try a hard refresh once.');
  }
};

void bootstrap();
